import { CancellationToken, IHttpApi, IHttpApiFetchConfig, IModelResponse } from '@cian/http-api';
import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { HttpBadStatusCodeError } from '@cian/peperrors/shared';
import { TMetaDeepJson } from '@cian/logger/shared';
import { IApplicationContext } from '../../types/applicationContext';
import { ERuntimeSettings } from '../../types/runtimeSettings';
import { extractStoreStateForRequestLog } from '../../utils/extractStoreStateForRequestLog';
import { UNKNOWN_REQUEST_REJECTION } from '../../constants/api';

export const createBaseQuery: TCreateBaseQueryFn = ({ httpApi, logger, config }) => {
  return async ({ fetchFunction, parameters, config: requestConfig, loggerInfo }, baseQueryApi) => {
    const cancellationToken = new CancellationToken();
    const cancelRequestCallback = () => cancellationToken.cancel();
    baseQueryApi?.signal.addEventListener('abort', cancelRequestCallback);
    const domain = baseQueryApi?.endpoint ?? 'unknown-request';
    const isShadowMode = config.get<boolean>(ERuntimeSettings.isShadowMode) ?? false;

    try {
      const { statusCode, response } = await fetchFunction({
        httpApi,
        parameters,
        config: {
          ...requestConfig,
          cancellationToken,
          requestConfig: {
            ...requestConfig?.requestConfig,
            headers: {
              ...requestConfig?.requestConfig?.headers,
              ...(isShadowMode && { 'X-ShadowMode': 'true' }),
            },
          },
        },
      });

      if (statusCode === 200) {
        return { data: response };
      }

      logger[statusCode === 400 ? 'warning' : 'error'](
        new HttpBadStatusCodeError({
          message: `Запрос ${domain} ответил с кодом ${statusCode}`,
          domain,
          statusCode,
        }),
        {
          description: loggerInfo.description,
          degradation: loggerInfo.degradation,
          parameters: JSON.stringify(loggerInfo.safeParameters),
          storeState: extractStoreStateForRequestLog(baseQueryApi?.getState()),
          response: JSON.stringify(response),
        },
      );

      return { error: response };
    } catch (error) {
      logger.error(error as Error, {
        domain,
        description: loggerInfo.description,
        degradation: loggerInfo.degradation,
        parameters: JSON.stringify(loggerInfo.safeParameters),
        storeState: extractStoreStateForRequestLog(baseQueryApi?.getState()),
      });

      return { error: UNKNOWN_REQUEST_REJECTION };
    } finally {
      baseQueryApi?.signal.removeEventListener('abort', cancelRequestCallback);
    }
  };
};

type TBaseQueryFn = BaseQueryFn<{
  fetchFunction: TFetchFunction;
  parameters?: unknown;
  config?: IHttpApiFetchConfig;
  /** Данные для логирования в случае ошибки */
  loggerInfo: {
    /** Описание запроса на русском */
    description: string;
    /** Описание деградации на русском */
    degradation?: string;
    /** Параметры запроса без чувствительных данных */
    safeParameters?: TMetaDeepJson;
  };
}>;

export type TFetchFunction = (params: {
  httpApi: IHttpApi;
  // oxlint-disable-next-line typescript/no-explicit-any
  parameters: any;
  config: IHttpApiFetchConfig;
}) => Promise<IModelResponse<unknown>>;

type TCreateBaseQueryFn = (
  context: IApplicationContext,
) => TBaseQueryFn & ((arg: Parameters<TBaseQueryFn>[0], api?: undefined, e?: undefined) => ReturnType<TBaseQueryFn>);
